import { useState, useEffect } from "react";
import { api } from "../../context";

// Module-level color vars — updated by Dashboard function on each render
let BORDER = "#1e2a3a";
let PRI    = "#4f8ef7";
let TEXT   = "#e6edf3";
let SUB    = "#7d8590";
let GRN    = "#3fb950";
let RED    = "#f85149";
let GOLD   = "#f0b429";
let CARD   = "#161b22";

const fmt = (n) => Number(n || 0).toLocaleString();

const StatCard = ({ icon, label, value, sub, color = PRI, onClick }) => (
  <div onClick={onClick} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", cursor: onClick ? "pointer" : "default", transition: "border-color 0.2s" }}
    onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = color)}
    onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = BORDER)}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: SUB, marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{icon}</div>
    </div>
  </div>
);

const MiniBar = ({ data = [], color, height = 40 }) => {
  const max = Math.max(...data.map(d => d.total || d.count || 0), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height }}>
      {data.slice(-20).map((d, i) => {
        const val = d.total || d.count || 0;
        const h = Math.max(2, (val / max) * (height - 4));
        return <div key={i} style={{ flex: 1, height: h, background: color, borderRadius: "2px 2px 0 0", opacity: 0.8, minWidth: 3 }} />;
      })}
    </div>
  );
};

export default function Dashboard({ onNavigate, t, isDark }) {
  // Update module-level vars so StatCard/MiniBar can use them
  BORDER = t.border;
  PRI    = t.pri;
  TEXT   = t.text;
  SUB    = t.sub;
  GRN    = t.grn;
  RED    = t.red;
  GOLD   = t.gold;
  CARD   = t.card;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/admin/dashboard").then(r => {
      if (r.ok) setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: SUB, fontSize: 14 }}>Loading dashboard...</div>
      </div>
    </div>
  );

  if (!data) return <div style={{ color: RED, padding: 40 }}>Failed to load dashboard data.</div>;

  // Compute MRR
  const premiumCount = data.plans?.premium || 0;
  const eliteCount   = data.plans?.elite || 0;
  const premiumMRR   = premiumCount * 499;
  const eliteMRR     = eliteCount * 999;
  const totalMRR     = premiumMRR + eliteMRR;

  // Expiring soon (placeholder — from subscribers)
  const expiringSoon = 2;

  const quickNav = [
    { icon: "👥", label: "Subscribers",   id: "subscribers" },
    { icon: "💰", label: "All Payments",   id: "revenue" },
    { icon: "❌", label: "Failed Payments",id: "revenue" },
    { icon: "💳", label: "Plans & Pricing",id: "plans" },
    { icon: "📈", label: "Analytics",      id: "analytics" },
    { icon: "🤝", label: "Engagement",     id: "engagement" },
    { icon: "🚦", label: "Traffic",        id: "traffic" },
    { icon: "🌍", label: "Demographics",   id: "demographics" },
    { icon: "💬", label: "Chats",          id: "chats" },
    { icon: "🎉", label: "Events",         id: "events" },
    { icon: "🛡️", label: "Verification",   id: "verification" },
    { icon: "⚠️", label: "Reports",        id: "reports" },
  ];

  return (
    <div>
      {/* Page title */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>Community Platform</div>
        <div style={{ fontSize: 12, color: SUB, marginTop: 4 }}>community.geilstore.com — live from MySQL</div>
      </div>

      {/* ── Revenue Banner ── */}
      <div style={{ background: "linear-gradient(135deg, #1a4a8a 0%, #2563eb 40%, #4f8ef7 100%)", borderRadius: 14, padding: "24px 32px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "40%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04))" }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Total Revenue Collected</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: "#fff" }}>
            KES <span style={{ color: "#4ade80" }}>{fmt(data.revenue)}</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 6 }}>
            Premium: KES {fmt(premiumCount * 499)} · Elite: KES {fmt(eliteCount * 999)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Failed Payments (Recoverable)</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#fca5a5" }}>KES {fmt(data.failed_amount)}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{data.failed_payments || 0} failed transactions</div>
        </div>
      </div>

      {/* ── Row 1 Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <StatCard icon="👤" label="Total Members"  value={fmt(data.total_users)}   sub={`+${data.new_today || 0} today`}        color={PRI}  onClick={() => onNavigate("users")} />
        <StatCard icon="⭐" label="Premium"         value={fmt(premiumCount)}       sub="KES 499/mo each"                          color={GRN}  onClick={() => onNavigate("subscribers")} />
        <StatCard icon="👑" label="Elite"           value={fmt(eliteCount)}         sub="KES 999/mo each"                          color={GOLD} onClick={() => onNavigate("subscribers")} />
        <StatCard icon="⚠️" label="Expiring Soon"  value={expiringSoon}            sub="Within 7 days"                            color={RED}  onClick={() => onNavigate("subscribers")} />
      </div>

      {/* ── Row 2 Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <StatCard icon="🆓" label="Free Members"        value={fmt(data.plans?.free)}      sub="No subscription"              color={SUB}  />
        <StatCard icon="💰" label="This Month Revenue"  value={`KES ${fmt(data.revenue_week)}`} sub="Last 7 days"            color={GRN}  onClick={() => onNavigate("revenue")} />
        <StatCard icon="✅" label="Successful Payments" value={fmt((data.total_users || 0) - (data.failed_payments || 0))} sub="All time" color={GRN} onClick={() => onNavigate("revenue")} />
        <StatCard icon="❌" label="Failed Payments"     value={fmt(data.failed_payments)}  sub={`KES ${fmt(data.failed_amount)} lost`} color={RED} onClick={() => onNavigate("revenue")} />
      </div>

      {/* ── MRR Section ── */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Monthly Recurring Revenue (MRR)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
          {[
            { label: "Premium MRR", value: premiumMRR, sub: `${premiumCount} × KES 499`, color: GRN },
            { label: "Elite MRR",   value: eliteMRR,   sub: `${eliteCount} × KES 999`,   color: GOLD },
            { label: "Total MRR",   value: totalMRR,   sub: "if all renew",               color: PRI },
          ].map((m, i) => (
            <div key={i} style={{ padding: "16px 24px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{m.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: m.color }}>KES {fmt(m.value)}</div>
              <div style={{ fontSize: 11, color: SUB, marginTop: 4 }}>{m.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Activity Row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        <StatCard icon="🟢" label="Active Today"    value={fmt(data.active_today)}   sub="Unique sessions"     color={GRN} />
        <StatCard icon="📅" label="Active This Week" value={fmt(data.active_week)}   sub="Last 7 days"         color={PRI} />
        <StatCard icon="📝" label="Posts Today"      value={fmt(data.posts_today)}   sub={`${fmt(data.total_posts)} total`} color="#8b5cf6" />
        <StatCard icon="💬" label="Messages Today"   value={fmt(data.msgs_today)}    sub={`${fmt(data.total_messages)} total`} color="#06b6d4" onClick={() => onNavigate("chats")} />
      </div>

      {/* ── Revenue Chart + AI Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* Revenue chart */}
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Revenue (30 days)</div>
            <div style={{ fontSize: 12, color: GRN, fontWeight: 700 }}>KES {fmt(data.revenue)}</div>
          </div>
          {data.revenue_by_day?.length > 0
            ? <MiniBar data={data.revenue_by_day} color={GRN} height={80} />
            : <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: SUB, fontSize: 12 }}>No revenue data yet</div>
          }
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ fontSize: 10, color: SUB }}>{data.revenue_by_day?.[0]?.date?.slice(5) || ""}</span>
            <span style={{ fontSize: 10, color: SUB }}>{data.revenue_by_day?.[data.revenue_by_day.length - 1]?.date?.slice(5) || ""}</span>
          </div>
        </div>

        {/* AI Stats */}
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🤖 Jilink AI</div>
          {[
            { label: "Chats Today",    value: data.jilink_today || 0,  color: "#8b5cf6" },
            { label: "Chats This Week",value: data.jilink_week || 0,   color: PRI },
            { label: "Unique Users",   value: data.jilink_users || 0,  color: GRN },
          ].map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none" }}>
              <span style={{ fontSize: 12, color: SUB }}>{s.label}</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Signups + Failed payments charts ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>New Signups (30 days)</div>
            <div style={{ fontSize: 12, color: PRI, fontWeight: 700 }}>+{data.new_today} today</div>
          </div>
          {data.signups_by_day?.length > 0
            ? <MiniBar data={data.signups_by_day} color={PRI} height={60} />
            : <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", color: SUB, fontSize: 12 }}>No signup data yet</div>
          }
        </div>

        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Failed Payments (30 days)</div>
            <div style={{ fontSize: 12, color: RED, fontWeight: 700 }}>{data.failed_payments} total</div>
          </div>
          {data.failed_by_day?.length > 0
            ? <MiniBar data={data.failed_by_day} color={RED} height={60} />
            : <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", color: SUB, fontSize: 12 }}>No failed payment data yet</div>
          }
        </div>
      </div>

      {/* ── Retention ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {[
          { label: "7-Day Retention", value: data.retention_7 || 0,  color: data.retention_7 >= 50 ? GRN : data.retention_7 >= 25 ? GOLD : RED },
          { label: "30-Day Retention", value: data.retention_30 || 0, color: data.retention_30 >= 50 ? GRN : data.retention_30 >= 25 ? GOLD : RED },
        ].map((r, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{r.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: r.color }}>{r.value}%</div>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: BORDER }}>
              <div style={{ height: 8, borderRadius: 4, background: r.color, width: `${r.value}%`, transition: "width 1s" }} />
            </div>
            <div style={{ fontSize: 11, color: SUB, marginTop: 6 }}>
              {r.value >= 50 ? "✅ Excellent" : r.value >= 25 ? "⚠️ Needs improvement" : "❌ Critical — users not returning"}
            </div>
          </div>
        ))}
      </div>

      {/* ── Today's Logins ── */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Today's Logins ({data.today_logins?.length || 0})</div>
          <div style={{ fontSize: 11, color: SUB }}>IP · Device · Source</div>
        </div>
        {!data.today_logins?.length
          ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB, fontSize: 13 }}>No logins yet today</div>
          : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                    {["User", "IP Address", "Device", "Browser", "OS", "Source", "Time"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: SUB, fontWeight: 600, fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.today_logins.slice(0, 20).map((l, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${BORDER}20` }}>
                      <td style={{ padding: "8px 12px", color: TEXT, fontWeight: 600 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span>{l.emoji}</span>
                          <span>{l.user}</span>
                          {l.plan !== "free" && <span style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 700, background: l.plan === "elite" ? `${GOLD}20` : `${GRN}20`, color: l.plan === "elite" ? GOLD : GRN }}>{l.plan}</span>}
                        </div>
                      </td>
                      <td style={{ padding: "8px 12px", color: SUB, fontFamily: "monospace" }}>{l.ip}</td>
                      <td style={{ padding: "8px 12px", color: SUB }}>{l.device}</td>
                      <td style={{ padding: "8px 12px", color: SUB }}>{l.browser}</td>
                      <td style={{ padding: "8px 12px", color: SUB }}>{l.os}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: l.source === "instagram" ? "#e91e8c20" : l.source === "whatsapp" ? "#25d36620" : l.source === "facebook" ? "#1877f220" : `${PRI}15`, color: l.source === "instagram" ? "#e91e8c" : l.source === "whatsapp" ? "#25d366" : l.source === "facebook" ? "#1877f2" : SUB }}>
                          {l.source}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: SUB }}>{l.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* ── Active Chatters ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>💬 Top Chatters Today</div>
          {!data.active_chatters?.length
            ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB, fontSize: 13 }}>No chats today yet</div>
            : data.active_chatters.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < data.active_chatters.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <span style={{ fontSize: 11, color: SUB, width: 20, textAlign: "right" }}>{i + 1}</span>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: `linear-gradient(135deg, #4f8ef7, #a23ad1)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{c.user?.avatar_emoji || "👤"}</div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT }}>{c.user?.name || "Unknown"}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#06b6d4" }}>{c.msg_count}</span>
                <span style={{ fontSize: 10, color: SUB }}>msgs</span>
              </div>
            ))}
        </div>

        {/* Traffic sources */}
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🚦 Traffic Sources (30 days)</div>
          {!data.traffic_sources?.length
            ? <div style={{ textAlign: "center", padding: "24px 0", color: SUB, fontSize: 13 }}>No traffic data yet — logins will appear here</div>
            : (() => {
              const total = data.traffic_sources.reduce((a, s) => a + s.count, 0);
              const sourceColors = { instagram: "#e91e8c", whatsapp: "#25d366", facebook: "#1877f2", google: "#ea4335", direct: SUB, twitter: "#1da1f2", tiktok: "#ff0050", telegram: "#0088cc" };
              return data.traffic_sources.map((s, i) => {
                const pct = total > 0 ? Math.round(s.count / total * 100) : 0;
                const color = sourceColors[s.utm_source] || PRI;
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: TEXT, textTransform: "capitalize" }}>{s.utm_source}</span>
                      <span style={{ fontSize: 12, color: SUB }}>{s.count} ({pct}%)</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: BORDER }}>
                      <div style={{ height: 6, borderRadius: 3, background: color, width: `${pct}%`, transition: "width 0.8s" }} />
                    </div>
                  </div>
                );
              });
            })()}
        </div>
      </div>

      {/* ── Quick Navigation ── */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>Quick Navigation</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {quickNav.map(item => (
            <button key={item.id} onClick={() => onNavigate(item.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "transparent", color: TEXT, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", textAlign: "left" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = PRI; e.currentTarget.style.color = PRI; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT; }}>
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}